import React, { useEffect, useState, useRef } from "react";
import "./Mid-Section-Mobile.scss";

//import icons
import { CiEdit } from "react-icons/ci";
import { FaArrowRightLong } from "react-icons/fa6";
import { SlLogout } from "react-icons/sl";
import { RiLockPasswordFill } from "react-icons/ri";
import { CgArrowsExchangeAlt } from "react-icons/cg";
import { MdDeleteOutline } from "react-icons/md";

//imports for user data
import { useSelector, useDispatch } from "react-redux";

//functions for data fetching
import {
  getProfile,
  editProfile,
  changeUserPassword,
  editProfileImage,
} from "../../../../redux/slices/sliceActions";

const MidSectionProfile = ({
  resumeTourAtStep,
  setSharedValue,
  isTourActive,
}) => {
  //data fetched from the store
  const dispatch = useDispatch();
  const { user, profile } = useSelector((state) => state.landlordslice);
  // Use the same extraction logic as PC
  const token = user?.accessToken;
  const id = user?.data?.tenantId;

  const [profileUpdated, setProfileUpdated] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getProfile({ userId: id })).then(() => {
        setProfileUpdated(true);
      });
    }
  }, [id]);

  //input values
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [profilePicture, setProfilePicture] = useState(null);

  // Phone number validation state
  const [phoneError, setPhoneError] = useState("");

  // Track selected new profile picture and its preview
  const [selectedProfilePic, setSelectedProfilePic] = useState(null);
  const [selectedProfilePicPreview, setSelectedProfilePicPreview] =
    useState(null);

  const [apartmentDetails, setApartmentDetails] = useState({});

  useEffect(() => {
    if (profileUpdated && profile.user_name) {
      const { firstName, lastName } = getFirstAndLastName(profile.user_name);
      setFirstName(firstName);
      setLastName(lastName);
      setEmail(profile.user_email || "");
      setPhoneNumber(profile.user_phone_number || "");
      setProfilePicture(profile.profile_picture || null);
      setSelectedProfilePic(null);
      setSelectedProfilePicPreview(null);
      setApartmentDetails({
        propertyName: profile?.tenant_details?.house_info?.property_name,
        houseNumber: profile?.tenant_details?.house_info?.house_number,
      });
    }
  }, [profileUpdated, profile.user_name]);

  const [selectedOption, setSelectedOption] = useState("");
  const [focus, setFocus] = useState({}); // Track focus state

  //upload image
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedProfilePic(file);
      setSelectedProfilePicPreview(URL.createObjectURL(file));
    }
  };

  //function to handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "phoneNumber":
        setPhoneError("");
        setPhoneNumber(value);
        break;
      case "oldPassword":
        setOldPassword(value);
        break;
      case "newPassword":
        setNewPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
      default:
        break;
    }
  };

  //focus
  const handleFocus = (inputName) => {
    setFocus({ ...focus, [inputName]: true });
  };

  const handleBlur = (inputName) => {
    setFocus({ ...focus, [inputName]: false });
  };

  // Modal state for confirm changes
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Password change state
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Profile update logic (no custom toasts)
  const handleProfileUpdate = async () => {
    const fullName = `${firstName} ${lastName}`.trim();
    await dispatch(
      editProfile({
        name: fullName,
        email,
        phone: `+254${phoneNumber.substring(1)}`, // Convert to international format
      })
    );
    if (selectedProfilePic) {
      await dispatch(
        editProfileImage({ id, token, image: selectedProfilePic })
      );
    }
    // Fetch profile again to refresh data
    if (id) {
      await dispatch(getProfile({ userId: id }));
    }
    setShowConfirmModal(false);
    setSelectedOption("");
  };

  const handleConfirmChanges = () => {
    // Validate Kenyan phone number format: starts with 07 or 01 and 10 digits
    if (/^(07\d{8}|01\d{8})$/.test(phoneNumber)) {
      setPhoneError("");
    } else {
      setPhoneError(
        "Phone number must be in the format 07XXXXXXXX or 01XXXXXXXX"
      );

      return;
    }

    setShowConfirmModal(true);
  };

  const handleCancelModal = () => {
    setShowConfirmModal(false);
  };

  // Password update logic (no custom toasts)
  const handlePasswordChange = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      alert("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      alert("New password and confirm password do not match");
      return;
    }
    if (oldPassword === newPassword) {
      alert("Old password and new password cannot be the same");
      return;
    }

    dispatch(changeUserPassword({ oldPassword, newPassword }))
      .unwrap()
      .then(() => {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setSelectedOption("");

        // If tour is active, resume at step 2 (overview) after password update
        if (isTourActive && resumeTourAtStep && setSharedValue) {
          setTimeout(() => {
            setSharedValue("Overview");
            resumeTourAtStep(2);
          }, 2000); // Give user time to see any success indication
        }
      })
      .catch((error) => {
        // Error handling - could be improved with better error messages
        console.error("Password change failed:", error);
      });
  };

  const handleCancelPasswordChange = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setSelectedOption("");
  };

  const handleCancelButton = () => {
    if (profile.user_name) {
      const { firstName, lastName } = getFirstAndLastName(profile.user_name);
      setFirstName(firstName);
      setLastName(lastName);
      setEmail(profile.user_email || "");
      setPhoneNumber(profile.user_phone_number || "");
    }
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setSelectedProfilePic(null);
    setSelectedProfilePicPreview(null);
    setSelectedOption("");
  };

  // Delete profile picture (before upload)
  const [showDeletePicModal, setShowDeletePicModal] = useState(false);

  const handleDeletePicture = () => {
    setShowDeletePicModal(true);
  };

  const handleConfirmDeletePicture = () => {
    if (profilePicture || selectedProfilePicPreview) {
      dispatch(editProfileImage({ remove: true }));
    }
    setSelectedProfilePic(null);
    setSelectedProfilePicPreview(null);
    setProfilePicture(null);
    setShowDeletePicModal(false);
  };

  const handleCancelDeletePicture = () => {
    setShowDeletePicModal(false);
  };

  return (
    <div className="profile-page">
      {selectedOption === "" ? (
        <>
          <div className="top-side">
            {selectedProfilePicPreview || profilePicture ? (
              <>
                <img
                  src={selectedProfilePicPreview || profilePicture}
                  alt="profile picture"
                  className="profile-picture"
                />
              </>
            ) : (
              <div className="image"></div>
            )}

            <div className="name">{profile.user_name}</div>
            <div className="email">{email}</div>
          </div>
          <div className="bottom-side">
            <div className="choices-container">
              <button
                style={{
                  margin: "0px",
                  padding: "0px",
                  backgroundColor: "transparent",
                  width: "100%",
                  border: "none",
                }}
                onClick={() => {
                  setSelectedOption("Profile");
                }}
              >
                <div className="choices">
                  <div className="icon">
                    <CiEdit fontSize={25} />
                  </div>
                  <div className="text">Edit Profile</div>
                  <div className="icon">
                    <FaArrowRightLong fontSize={20} />
                  </div>
                </div>
              </button>
              <button
                style={{
                  margin: "0px",
                  padding: "0px",
                  backgroundColor: "transparent",
                  width: "100%",
                  border: "none",
                }}
                onClick={() => {
                  setSelectedOption("Plans");
                }}
              >
                {/* <div className="choices">
                  <div className="icon">
                    <CgArrowsExchangeAlt fontSize={25} />
                  </div>
                  <div className="text">Change Plan</div>
                  <div className="icon">
                    <FaArrowRightLong fontSize={20} />
                  </div>
                </div> */}
              </button>
              <button
                style={{
                  margin: "0px",
                  padding: "0px",
                  backgroundColor: "transparent",
                  width: "100%",
                  border: "none",
                }}
                onClick={() => {
                  setSelectedOption("Password");
                }}
              >
                <div className="choices">
                  <div className="icon" style={{ marginTop: "1.5%" }}>
                    <RiLockPasswordFill fontSize={21} />
                  </div>

                  <div className="text">Change Password</div>
                  <div className="icon">
                    <FaArrowRightLong fontSize={20} />
                  </div>
                </div>
              </button>
              <button
                style={{
                  margin: "0px",
                  padding: "0px",
                  backgroundColor: "transparent",
                  width: "100%",
                  border: "none",
                }}
                onClick={() => {
                  setSelectedOption("Logout");
                }}
              >
                <div className={`choices no-border`}>
                  <div className="icon" style={{ marginTop: "2.5%" }}>
                    <SlLogout fontSize={20} />
                  </div>
                  <div className="text">Logout</div>
                  <div className="icon" style={{ marginTop: "1.5%" }}>
                    <FaArrowRightLong fontSize={20} />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {selectedOption === "Profile" && (
            <div className="edit-profile">
              <div className="top-side-profile">
                <div className="porfile-image" style={{ position: "relative" }}>
                  {selectedProfilePicPreview || profilePicture ? (
                    <img
                      src={selectedProfilePicPreview || profilePicture}
                      alt="profile"
                      className="profile-picture"
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        zIndex: 0,
                      }}
                    />
                  ) : null}
                  <div className="edit-icon" onClick={handleClick}>
                    <CiEdit fontSize={20} />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "block" }}
                    onChange={handleChange}
                  />
                  {selectedProfilePicPreview || profilePicture ? (
                    <div
                      className="edit-icon"
                      style={{
                        left: "1%",
                        // background: "#e74c3c",
                        color: "#fff",
                        zIndex: 2,
                      }}
                      onClick={handleDeletePicture}
                    >
                      <MdDeleteOutline />
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="bottom-side-profile">
                <div className="fields-halves">
                  <div className="individual-half">
                    <label
                      htmlFor="first-name-input"
                      className={`text ${
                        focus["first-name-input"] ? "focus" : ""
                      }`}
                    >
                      First Name:
                    </label>
                    <input
                      type="text"
                      id="first-name-input"
                      value={firstName}
                      name="firstName"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("first-name-input")}
                      onBlur={() => handleBlur("first-name-input")}
                      onChange={handleInputChange}
                      placeholder="First Name eg John"
                    />
                  </div>
                  <div className="individual-half">
                    <label
                      htmlFor="last-name-input"
                      className={`text ${
                        focus["last-name-input"] ? "focus" : ""
                      }`}
                    >
                      Last Name:
                    </label>
                    <input
                      type="text"
                      id="last-name-input"
                      value={lastName}
                      name="lastName"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("last-name-input")}
                      onBlur={() => handleBlur("last-name-input")}
                      onChange={handleInputChange}
                      placeholder="Last Name eg Doe"
                    />
                  </div>
                </div>
                <div className="fields-halves">
                  <div className={`individual-half margin-top`}>
                    <label
                      htmlFor="email-input"
                      className={`text ${focus["email-input"] ? "focus" : ""}`}
                    >
                      email
                    </label>
                    <input
                      type="text"
                      id="email-input"
                      value={email}
                      name="email"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("email-input")}
                      onBlur={() => handleBlur("email-input")}
                      onChange={handleInputChange}
                      placeholder="Email eg example@example.com"
                    />
                  </div>
                </div>
                <div className="fields-halves">
                  <div className={`individual-half margin-top`}>
                    <label
                      htmlFor="phone-number-input"
                      className={`text ${
                        focus["phone-number-input"] ? "focus" : ""
                      }`}
                    >
                      Phone Number
                    </label>
                    <input
                      type="text"
                      id="phone-number-input"
                      value={phoneNumber}
                      placeholder="Phone Number eg 07XXXXXXXX or 01XXXXXXXX"
                      name="phoneNumber"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("phone-number-input")}
                      onBlur={() => handleBlur("phone-number-input")}
                      onChange={handleInputChange}
                    />
                    {phoneError && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "0.9rem",
                          marginTop: "0.3rem",
                        }}
                      >
                        {phoneError}
                      </div>
                    )}
                  </div>
                </div>
                <div className="apartment-details">
                  <div className="title">{apartmentDetails?.propertyName}</div>
                  <div className="house-details">
                    House Number: {apartmentDetails?.houseNumber}
                  </div>
                </div>
                <div className="profile-buttons-container">
                  <button
                    className="confirm-button"
                    onClick={handleConfirmChanges}
                  >
                    Confirm changes
                  </button>
                  <button
                    className="confirm-button"
                    onClick={handleCancelButton}
                  >
                    Cancel and Exit
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedOption === "Password" && (
            <>
              <div className="password-change-container">
                <div className="title-of-page">
                  <div className="">Change Password</div>
                  <div className="sub-text">
                    Change your password by entering the old password first,
                    then the new one
                  </div>
                </div>
                <div className="inputs-container">
                  <div className="individual-half">
                    <label
                      htmlFor="change-password-input"
                      className={`text ${
                        focus["change-password-input"] ? "focus" : ""
                      }`}
                    >
                      Old Password
                    </label>
                    <input
                      type="password"
                      id="change-password-input"
                      value={oldPassword}
                      className="input"
                      name="oldPassword"
                      autoComplete="off"
                      onFocus={() => handleFocus("change-password-input")}
                      onBlur={() => handleBlur("change-password-input")}
                      onChange={handleInputChange}
                      placeholder="Enter your old password"
                    />
                  </div>
                  <div className={"individual-half margin-top"}>
                    <label
                      htmlFor="new-password-input"
                      className={`text ${
                        focus["new-password-input"] ? "focus" : ""
                      }`}
                    >
                      New password
                    </label>
                    <input
                      type="password"
                      id="new-password-input"
                      value={newPassword}
                      name="newPassword"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("new-password-input")}
                      onBlur={() => handleBlur("new-password-input")}
                      onChange={handleInputChange}
                      placeholder="Enter your new password"
                    />
                  </div>
                  <div className={"individual-half margin-top"}>
                    <label
                      htmlFor="confirm-password-input"
                      className={`text ${
                        focus["confirm-password-input"] ? "focus" : ""
                      }`}
                    >
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      id="confirm-password-input"
                      value={confirmPassword}
                      name="confirmPassword"
                      className="input"
                      autoComplete="off"
                      onFocus={() => handleFocus("confirm-password-input")}
                      onBlur={() => handleBlur("confirm-password-input")}
                      onChange={handleInputChange}
                      placeholder="Confirm your new password"
                    />
                  </div>
                </div>
                <div className="profile-buttons-container">
                  <button
                    className="confirm-button"
                    onClick={handlePasswordChange}
                  >
                    Confirm changes
                  </button>
                  <button
                    className="confirm-button"
                    onClick={handleCancelPasswordChange}
                  >
                    Cancel and Exit
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Slide-up confirmation modal */}
          {showConfirmModal && (
            <div className="modal-overlay-mobile">
              <div className="modal-content-mobile">
                <div className="title">Confirm Profile Changes</div>
                <div className="sub-title">
                  Are you sure you want to update your profile with the
                  following information?
                </div>
                <div className="modal-details">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    {selectedProfilePicPreview || profilePicture ? (
                      <img
                        src={selectedProfilePicPreview || profilePicture}
                        alt="profile preview"
                        className="profile-picture"
                        style={{ width: 60, height: 60, borderRadius: "50%" }}
                      />
                    ) : (
                      <div
                        className="profile-pic"
                        style={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background: "#192a3a",
                        }}
                      ></div>
                    )}
                    <span>Profile Picture</span>
                  </div>
                  <div className="sub-title">
                    <span
                      style={{
                        color: "black",
                        fontFamily: "'Karla', sans-serif",
                        fontSize: "18px",
                      }}
                    >
                      Name:
                    </span>{" "}
                    {firstName} {lastName}
                  </div>
                  <div className="sub-title">
                    <span
                      style={{
                        color: "black",
                        fontFamily: "'Karla', sans-serif",
                        fontSize: "18px",
                      }}
                    >
                      Email:
                    </span>{" "}
                    {email}
                  </div>
                  <div className="sub-title">
                    <span
                      style={{
                        color: "black",
                        fontFamily: "'Karla', sans-serif",
                        fontSize: "18px",
                      }}
                    >
                      Phone:
                    </span>{" "}
                    {phoneNumber}
                  </div>
                </div>
                <div className="modal-buttons">
                  <button className="confirm-btn" onClick={handleProfileUpdate}>
                    Yes, Update Profile
                  </button>
                  <button className="cancel-btn" onClick={handleCancelModal}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Delete Picture Confirmation Modal */}
          {showDeletePicModal && (
            <div className="modal-overlay-mobile">
              <div className="modal-content-mobile">
                <div className="title">Delete Profile Picture</div>
                <div className="sub-title">
                  Are you sure you want to remove your profile picture?
                </div>
                <div className="modal-buttons">
                  <button
                    className="confirm-btn"
                    onClick={handleConfirmDeletePicture}
                  >
                    Yes, Delete Picture
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={handleCancelDeletePicture}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

function getFirstAndLastName(userName) {
  // Split the user name by space to separate the first and last names
  const nameParts = userName.split(" ");
  if (nameParts.length >= 2) {
    const firstName = nameParts[0];
    const lastName = nameParts[1];
    return { firstName, lastName };
  } else {
    return { firstName: nameParts[0], lastName: null };
  }
}

export default MidSectionProfile;
